import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bgGradient from '../../assets/images/eunpyeongcare/bg-gradient.png';
import eunpyeongLogo from '../../assets/images/eunpyeongcare/eunpyeong-logo.png';
import headsetImage from '../../assets/images/eunpyeongcare/headset.png';
import soundpillLogo from '../../assets/images/eunpyeongcare/soundpill-logo.png';
import CollabXIcon from '../../components/eunpyeongcare/CollabXIcon';
import EunpyeongCareNoticeModal from '../../components/eunpyeongcare/EunpyeongCareNoticeModal';
import SimpleLoading from '../../components/SimpleLoading';
import { AuthStateContext } from '../../contexts/AuthContext';
import { getItem, USERINFO, USERTOKEN } from '../../utils/localStorage';
import {
  applyEunpyeongCareCoupon,
  epLocalStorageHandler,
  getEunpyeongErrorNotice,
  openEunpyeongCareApp,
  openEunpyeongCareStore,
  type EunpyeongCouponApplyResult,
} from '../../utils/eunpyeongCare';
import type { User } from '../../types';
import './eunpyeongcare-coupon-page.css';

type ViewResult = EunpyeongCouponApplyResult | { status: 'open_app'; couponCode: string };

const ALREADY_USED_NOTICE = {
  title: '이미 사용하신 이용권입니다.',
  message: '은평구 이용권은 계정당 1회 사용 가능합니다. 감사합니다.',
};

const RELOGIN_NOTICE_TITLE = '사운드필 앱 설치 후, 현재 계정으로 재로그인 해주세요';

function platformLabel(type: string | undefined | null): string {
  switch ((type || '').trim().toLowerCase()) {
    case 'kakao':
      return '카카오';
    case 'google':
      return '구글';
    case 'naver':
      return '네이버';
    case 'apple':
      return '애플';
    case 'facebook':
      return '페이스북';
    case 'email':
      return '이메일';
    default:
      return type?.trim() || '소셜';
  }
}

function buildReloginDescription(user: User | null | undefined): string {
  const stored = getItem(USERINFO) as User | null;
  const email = user?.email?.trim() || stored?.email?.trim() || '이메일 정보 없음';
  const platform = platformLabel(user?.type || stored?.type);
  return `계정: ${email}\n로그인 방식: ${platform}`;
}

function resultCopy(result: ViewResult) {
  switch (result.status) {
    case 'used':
      return {
        title: '이용권이 적용되었어요',
        body: '앱을 설치하면 바로 이용할 수 있어요.',
      };
    case 'registered':
      return {
        title: '이용권이 등록되었어요',
        body: '지금 구독 중이라 보관함에 넣어 두었어요. 구독이 끝나면 사용할 수 있어요.',
      };
    case 'already_registered':
    case 'open_app':
    case 'error':
      return {
        title: '앱에서 이어서 이용하세요',
        body: '사운드필 앱을 열면 이용권을 확인할 수 있어요.',
      };
    default:
      return {
        title: '앱에서 이어서 이용하세요',
        body: '사운드필 앱을 열면 이용권을 확인할 수 있어요.',
      };
  }
}

function noticeFromResult(result: ViewResult, t: (key: string) => string) {
  if (result.status === 'already_registered') {
    return ALREADY_USED_NOTICE;
  }
  if (result.status === 'error') {
    return getEunpyeongErrorNotice(result.errorCode, t);
  }
  return null;
}

export default function EunpyeongCareCompletePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthentication, user } = useContext(AuthStateContext);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<ViewResult | null>(null);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [reloginOpen, setReloginOpen] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!isAuthentication && !getItem(USERTOKEN)) {
      navigate('/eunpyeongcare/coupon', { replace: true });
      return;
    }
    if (!isAuthentication) return;
    if (startedRef.current) return;
    startedRef.current = true;

    const couponCode = epLocalStorageHandler('GET');
    if (typeof couponCode !== 'string' || !couponCode.trim()) {
      setResult({ status: 'open_app', couponCode: '' });
      setLoading(false);
      setReloginOpen(true);
      return;
    }

    (async () => {
      const applied = await applyEunpyeongCareCoupon(couponCode);
      setResult(applied);
      setLoading(false);
      if (applied.status !== 'error') {
        epLocalStorageHandler('DELETE');
      }
      if (applied.status === 'error' || applied.status === 'already_registered') {
        setNoticeOpen(true);
      } else {
        setReloginOpen(true);
      }
    })();
  }, [isAuthentication, navigate]);

  const copy = result ? resultCopy(result) : null;
  const notice = result ? noticeFromResult(result, t) : null;
  const reloginDescription = buildReloginDescription(user);

  const handleOpenApp = () => {
    setReloginOpen(true);
  };

  const handleReloginConfirm = () => {
    setReloginOpen(false);
    epLocalStorageHandler('DELETE');
    openEunpyeongCareApp();
  };

  const handleNoticeConfirm = () => {
    const alreadyUsed =
      result?.status === 'already_registered' ||
      notice?.title === '이미 사용하신 이용권입니다.';
    setNoticeOpen(false);
    if (!alreadyUsed) return;
    epLocalStorageHandler('DELETE');
    openEunpyeongCareStore();
  };

  return (
    <>
      {loading && <SimpleLoading />}
      {noticeOpen && notice ? (
        <EunpyeongCareNoticeModal
          title={notice.title}
          message={notice.message}
          onConfirm={handleNoticeConfirm}
        />
      ) : null}
      {reloginOpen && !noticeOpen ? (
        <EunpyeongCareNoticeModal
          title={RELOGIN_NOTICE_TITLE}
          message={reloginDescription}
          onConfirm={handleReloginConfirm}
        />
      ) : null}
      <div className="eunpyeongcare-page">
        <div className="eunpyeongcare-page__shell">
          <section className="eunpyeongcare-page__hero" aria-label="은평구 사운드필 이용권 결과">
            <img src={bgGradient} alt="" className="eunpyeongcare-page__hero-bg" />
            <div className="eunpyeongcare-page__hero-content">
              <div className="eunpyeongcare-page__card-glow" aria-hidden />
              <div className="eunpyeongcare-page__card">
                <div className="eunpyeongcare-page__logos">
                  <div
                    className="eunpyeongcare-page__logo-eunpyeong"
                    role="img"
                    aria-label="북한산큰숲, 은평"
                    style={{ backgroundImage: `url(${eunpyeongLogo})` }}
                  />
                  <CollabXIcon />
                  <img
                    src={soundpillLogo}
                    alt="SoundPill"
                    className="eunpyeongcare-page__logo-soundpill"
                  />
                </div>
                {copy ? (
                  <>
                    <h1 className="eunpyeongcare-page__headline">{copy.title}</h1>
                    <p className="eunpyeongcare-page__greeting eunpyeongcare-page__result-body">{copy.body}</p>
                  </>
                ) : (
                  <>
                    <p className="eunpyeongcare-page__greeting">은평구 주민 여러분,</p>
                    <h1 className="eunpyeongcare-page__headline">
                      이용권을
                      <br />
                      확인하고 있어요
                    </h1>
                  </>
                )}
              </div>
              <div className="eunpyeongcare-page__headset-wrap">
                <img src={headsetImage} alt="" className="eunpyeongcare-page__headset" />
              </div>
            </div>
          </section>

          <section className="eunpyeongcare-page__login" aria-label="앱 열기">
            {result && !loading ? (
              <button type="button" className="eunpyeongcare-page__cta" onClick={handleOpenApp}>
                앱 열기
              </button>
            ) : null}
          </section>
        </div>
      </div>
    </>
  );
}
